import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import axios from 'axios';
import { API } from '../../services/api';
import { FileText, Download, ExternalLink, Loader2 } from 'lucide-react';

const InsemNotes = ({ year, branch, semester, subject, type }) => {
  const [pdfs, setPdfs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPdfs = async () => {
      if (!year || !branch || !semester || !subject || !type) {
        return;
      }

      try {
        setLoading(true);
        setError(null);
        
        const params = new URLSearchParams({
          year,
          branch,
          semester,
          subject,
          type
        });

        const response = await axios.get(`${API.GET_PDFS}?${params.toString()}`);
        setPdfs(response.data || []);
        
        if (response.data && response.data.length === 0) {
          setError('No insem notes found for the selected criteria');
        }
      } catch (err) {
        console.error('Error fetching PDFs:', err);
        setError(err.response?.data?.message || 'Failed to fetch insem notes');
        toast.error('Failed to load insem notes');
      } finally {
        setLoading(false);
      }
    };

    fetchPdfs();
  }, [year, branch, semester, subject, type]);

  const handleDownload = (pdfId, pdfTitle) => {
    window.open(API.DOWNLOAD_PDF(pdfId), '_blank', 'noopener,noreferrer');
    toast.success(`Downloading ${pdfTitle}`);
  };

  if (loading) {
    return (
      <section className="bg-gradient-to-br from-gray-900/60 to-gray-900/40 border border-gray-800 rounded-2xl p-6">
        <div className="flex flex-col items-center justify-center py-12">
          <Loader2 className="w-12 h-12 text-indigo-500 animate-spin mb-4" />
          <p className="text-gray-400 text-sm">Loading insem notes...</p>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="bg-gradient-to-br from-gray-900/60 to-gray-900/40 border border-gray-800 rounded-2xl p-6">
        <div className="flex flex-col items-center justify-center py-12">
          <div className="p-4 rounded-full bg-amber-500/10 border border-amber-500/20 mb-4">
            <FileText className="w-8 h-8 text-amber-400" />
          </div>
          <p className="text-gray-400 text-sm text-center">{error}</p>
        </div>
      </section>
    );
  }

  if (pdfs.length === 0) {
    return (
      <section className="bg-gradient-to-br from-gray-900/60 to-gray-900/40 border border-gray-800 rounded-2xl p-6">
        <div className="flex flex-col items-center justify-center py-12">
          <div className="p-4 rounded-full bg-gray-800/60 border border-gray-700 mb-4">
            <FileText className="w-8 h-8 text-gray-500" />
          </div>
          <p className="text-gray-400 text-sm">No insem notes available</p>
          <p className="text-gray-500 text-xs mt-1">Check back later or try different criteria</p>
        </div>
      </section>
    );
  }

  return (
    <section className="bg-gradient-to-br from-gray-900/60 to-gray-900/40 border border-gray-800 rounded-2xl p-6">
      <header className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-xl sm:text-2xl font-bold text-white">Insem Notes</h3>
            <p className="text-sm text-gray-400 mt-1">
              {pdfs.length} {pdfs.length === 1 ? 'note' : 'notes'} available
            </p>
          </div>
          <div className="p-2 rounded-lg bg-indigo-500/10 border border-indigo-500/20">
            <FileText className="w-6 h-6 text-indigo-400" />
          </div>
        </div>
      </header>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {pdfs.map((pdf) => (
          <article
            key={pdf._id}
            className="group relative bg-slate-800/60 hover:bg-slate-800/80 border border-gray-700 hover:border-indigo-500/50 rounded-xl p-5 transition-all duration-300 hover:shadow-xl hover:shadow-indigo-500/10"
          >
            <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/0 to-purple-500/0 group-hover:from-indigo-500/5 group-hover:to-purple-500/5 rounded-xl transition-all duration-300" />
            
            <div className="relative">
              <div className="flex items-start justify-between gap-3 mb-3">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <FileText className="w-4 h-4 text-indigo-400 flex-shrink-0" />
                    <span className="text-xs font-medium text-indigo-400 uppercase tracking-wide">
                      {pdf.subtype || 'PDF'}
                    </span>
                  </div>
                  <h4 className="font-semibold text-white text-sm group-hover:text-indigo-300 transition-colors line-clamp-2">
                    {pdf.subject}
                  </h4>
                </div>
              </div>

              <div className="flex flex-wrap gap-2 mb-4">
                <span className="px-2 py-1 rounded-md bg-gray-700/50 text-xs text-gray-300">
                  {pdf.year}
                </span>
                <span className="px-2 py-1 rounded-md bg-gray-700/50 text-xs text-gray-300">
                  {pdf.semester}
                </span>
                {pdf.downloads > 0 && (
                  <span className="px-2 py-1 rounded-md bg-indigo-500/10 border border-indigo-500/30 text-xs text-indigo-400 flex items-center gap-1">
                    <Download className="w-3 h-3" />
                    {pdf.downloads}
                  </span>
                )}
              </div>

              <button
                onClick={() => handleDownload(pdf._id, pdf.subject)}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-500 hover:to-purple-500 text-white text-sm font-semibold rounded-lg transition-all transform group-hover:scale-105"
              >
                <Download className="w-4 h-4" />
                Download
                <ExternalLink className="w-3 h-3 ml-auto" />
              </button>
            </div>
          </article>
        ))}
      </div>
    </section>
  );
};

export default InsemNotes;
